import React from 'react';
import {
  convertTimestampToDate,
  formatDate,
  useOffer,
} from '../../utils/helpers';

import { PlayIcon } from '../Icons';
import { ButtonLink } from '../common';
import { useVisitId } from '../../hooks/useVisitId';
import useContextToken from '../../hooks/useContextToken';

import './EpisodeRow.scss';

import { IEpisode, IOffer } from '../../utils/models';

// TEALIUM
import initTealiumTracker from 'tealium-tracker';
import AccessServiceBadge from '../AccessServiceBadge';
import { KiteButton } from '@kite/react-kite';
let tealiumTrackEvent;
if (typeof window !== 'undefined') {
  const { trackEvent } = initTealiumTracker({ debugMode: true });
  tealiumTrackEvent = trackEvent;
}

const renderEpisodeTitle = (episodeNum: number, episodeTitle: string) => {
  if (episodeNum && episodeTitle) {
    return `${episodeNum}. ${episodeTitle}`;
  }

  if (episodeNum) {
    return `Episode ${episodeNum}`;
  }

  if (episodeTitle) {
    return episodeTitle;
  }
};

export interface IEpisodeRowProps {
  episode: IEpisode;
  analytics: any;
  index: number;
}

const EpisodeRow = ({
  episode: {
    id,
    episodeTitle,
    seriesTitle,
    imageUrl,
    episodeNum,
    seasonNum,
    description,
    releaseDate,
    offer,
    accessServices,
    isAvailableOnDemand,
  },
  index,
  analytics,
}: IEpisodeRowProps) => {
  const visitId = useVisitId();
  const { contextToken } = useContextToken();

  const hasAccessibleFeatures = !!accessServices?.length;

  const quantumTrack = () => {
    analytics.track(
      'SpectrumOnDemand_selectContent_related_tvShows_assetSelection',
      {
        currPageElemUiName: 'tvShows',
        currPageElemIndex: index,
        tmsProgramId: id,
      }
    );
  };

  const handleEpisodeClick = () => {
    analytics.track(
      'SpectrumOnDemand_selectContent_related_tvShows_assetSelection',
      {
        currPageElemUiName: 'tvShows',
        currPageElemIndex: index,
        tmsProgramId: id,
      }
    );
    tealiumTrackEvent({
      linktype: 'tvEpisodeExitToWatch',
      linkname: `${seriesTitle}__episode-${episodeNum}`,
    });
  };

  const { providerId, movieAssetId, startTime, endTime } = useOffer(
    offer as unknown as IOffer[]
  );
  const episodeLink = `https://watch.spectrum.net/ondemand/play/${providerId}::${movieAssetId}?visitId=${visitId}${contextToken}`;

  return (
    <article className="episode-row">
      <div className="episode-row__content">
        <h3
          className="title"
          aria-label={`${
            episodeNum
              ? `Episode ${episodeNum} ${episodeTitle || ''}`
              : episodeTitle || ''
          }`}
        >
          {renderEpisodeTitle(episodeNum || 0, episodeTitle || '')}
        </h3>

        {/* Air Date */}
        <div className="dates">
          {releaseDate && (
            <h4 className="dates__detail">
              <span className="label">Air Date </span> {formatDate(releaseDate)}
            </h4>
          )}
        </div>

        {/* Episode Description */}
        {description && <p className="description">{description}</p>}

        {/* Accessible Features List */}
        {!!hasAccessibleFeatures && (
          <span className="episode-row__accessible-features">
            <h4 className="label">Accessible Features</h4>
            <div className="list">
              {accessServices.map((serviceType) => (
                <AccessServiceBadge type={serviceType} key={serviceType} />
              ))}
            </div>
          </span>
        )}

        {/* Buttons */}
        <div className="episode-row__action-buttons">
          {!!isAvailableOnDemand && (
            <ButtonLink
              type="primary"
              path={episodeLink}
              onClick={handleEpisodeClick}
              leftIcon="ki-play-circle"
              ariaLabel={`Watch ${seriesTitle} - ${
                seasonNum && `Season ${seasonNum}`
              } ${
                episodeNum
                  ? `Episode ${episodeNum} ${episodeTitle || ''}`
                  : episodeTitle || ''
              }`}
            >
              Watch Online
            </ButtonLink>
          )}
          {!!hasAccessibleFeatures && (
            <ButtonLink
              type="secondary"
              path="/access"
              gatsbyLink
              rightIcon="ki-chevron-right"
              ariaLabel="Learn about accessible features on Spectrum Access"
            >
              Download Spectrum Access
            </ButtonLink>
          )}
        </div>
      </div>
    </article>
  );
};

export default EpisodeRow;
